import { ColorThresholdInput } from "@/overlay/components/CommonParts/Threshold/ColorThresholdInput";
import type { AdvancedThreshold, ThresholdColor } from "@/overlay/components/CommonParts/Threshold/types";
import { useThresholdManager } from "@/overlay/components/CommonParts/Threshold/useThresholdManager";
import { Button, ButtonBar } from "@/overlay/components/CommonParts/buttons";
import { DefaultThreshold } from "@/reducer/client-models";
import { Trans } from "@lingui/react/macro";
import { useFlag } from "@openfeature/react-sdk";
import { type DraggableItemIdentifier, SortableList } from "../SortableList";

const CompleteThreshold = (props: {
  updateThresholds: (newThresholds: ThresholdColor[]) => void;
  updateAdvancedThresholds: (newAdvancedThresholds: AdvancedThreshold[]) => void;
  thresholds: ThresholdColor[];
  advancedThresholds: AdvancedThreshold[];
  paramUnit?: string;
  enableAdvancedThreshold?: boolean;
  isPoi?: boolean;
  onMoveIndex?: (dragStart: DraggableItemIdentifier, dropTarget: DraggableItemIdentifier) => void;
}) => {
  const { value: advancedThresholdFeature } = useFlag("advanced_threshold", false);
  const addThreshold = () => {
    props.updateThresholds([...props.thresholds, DefaultThreshold]);
  };

  function changeThreshold(propsValue: Partial<ThresholdColor>, idx: number) {
    if (props.thresholds[idx]) {
      const tmpThresholds = [...props.thresholds];
      tmpThresholds[idx] = { ...tmpThresholds[idx], ...propsValue };
      props.updateThresholds(tmpThresholds);
    }
  }

  function removeThreshold(idx: number) {
    if (props.thresholds[idx]) {
      const newArray = props.thresholds.filter((_threshold, index) => index !== idx);
      props.updateThresholds(newArray);
    }
  }
  const openThresholdManager = useThresholdManager(
    props.advancedThresholds,
    props.updateAdvancedThresholds,
    props.isPoi,
  );
  return (
    <>
      <div className={"form-field"}>
        <span className="form-field form-field--no-semantic-link">
          <span />
          <span>
            <Trans>Thresholds</Trans>:
          </span>
        </span>
      </div>

      {!props.advancedThresholds.length && (
        <SortableList
          className="box-section--breakout"
          onSwapItems={props.onMoveIndex}
          items={props.thresholds.map((threshold, idx) => ({
            id: idx,
            content: (
              <ColorThresholdInput
                key={`${idx}_${threshold.color}`}
                threshold={threshold}
                onThresholdChange={(data) => changeThreshold(data, idx)}
                onRemove={() => removeThreshold(idx)}
                step={props.paramUnit?.includes("dust_aod") ? 0.1 : 1}
                allowDecimals={true}
              />
            ),
          }))}
        />
      )}
      {advancedThresholdFeature && props.enableAdvancedThreshold ? (
        <>
          <ButtonBar align={"center"} className="layer-stack-thresholds">
            <Button onClick={() => openThresholdManager()} icon={"data_thresholding"} iconStyle={"outlined"}>
              <Trans>Advanced Thresholds</Trans>
              <span className="material-icons">navigate_next</span>
            </Button>
          </ButtonBar>
          {!props.advancedThresholds.length && (
            <ButtonBar align={"center"} className="layer-stack-thresholds">
              <Button onClick={() => addThreshold()}>
                <Trans>Add Threshold</Trans>
                <span className="material-icons">add</span>
              </Button>
            </ButtonBar>
          )}
        </>
      ) : (
        (props?.thresholds.length || 0) < 10 && (
          <ButtonBar align={"right"} className="layer-stack-thresholds">
            <Button disabled={!!props.advancedThresholds.length} onClick={() => addThreshold()}>
              <Trans>Add Threshold</Trans>
              <span className="material-icons">add</span>
            </Button>
          </ButtonBar>
        )
      )}
    </>
  );
};

export default CompleteThreshold;
